const SKIP_BELOW_BYTES = 300 * 1024;
const DEFAULT_MAX_DIMENSION = 700;
const DEFAULT_QUALITY = 0.75;

function extensionForType(type: string): string {
  if (type === 'image/png') return 'png';
  if (type === 'image/webp') return 'webp';
  return 'jpg';
}

function withExtension(name: string, ext: string): string {
  const base = name.replace(/\.[^./\\]+$/, '');
  return `${base}.${ext}`;
}

/**
 * Resizes and recompresses an image File in the browser before it's uploaded,
 * so slow mobile/PC connections don't have to transfer a multi-MB phone photo
 * just to have it shrunk server-side afterwards (api/src/utils/imageOptimizer.ts
 * still runs as a safety net, but with this in place it becomes a near no-op).
 *
 * Non-image files (PDFs, etc.) and already-small images are returned unchanged.
 * Any decode/encode failure falls back to the original file — this is a
 * best-effort speed optimization, never a gate on the upload succeeding.
 */
export async function compressImageFile(
  file: File,
  opts: { maxDimension?: number; quality?: number } = {}
): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
    return file;
  }
  if (file.size < SKIP_BELOW_BYTES) {
    return file;
  }

  const maxDimension = opts.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const quality = opts.quality ?? DEFAULT_QUALITY;
  const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';

  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, outputType, outputType === 'image/jpeg' ? quality : undefined);
    });

    if (!blob || blob.size >= file.size) {
      return file;
    }

    return new File([blob], withExtension(file.name, extensionForType(outputType)), {
      type: outputType,
      lastModified: Date.now(),
    });
  } catch (error) {
    console.warn('Image compression failed; uploading original file', error);
    return file;
  }
}

/** Convenience helper for multi-file inputs — compresses every file in parallel. */
export async function compressImageFiles(
  files: File[],
  opts?: { maxDimension?: number; quality?: number }
): Promise<File[]> {
  return Promise.all(files.map((file) => compressImageFile(file, opts)));
}
